/** Bounded shared feedback. Callers retain their staged state when a write fails. */
import { create } from "zustand";

export interface ToastInput {
  key: string;
  kind: "undo" | "error" | "info";
  message: string;
  actionLabel?: string;
  action?: () => unknown;
  href?: string;
  durationMs?: number;
}
export interface Toast extends ToastInput {
  id: string;
  order: number;
  expiresAt: number;
}
const priority = { undo: 3, error: 2, info: 1 };
let order = 0;
export const visibleToasts = (toasts: Toast[]) =>
  [...toasts].sort((a, b) => priority[b.kind] - priority[a.kind] || b.order - a.order).slice(0, 3);
interface FeedbackState {
  toasts: Toast[];
  show: (input: ToastInput) => string;
  dismiss: (id: string) => void;
  visible: () => Toast[];
  expire: () => void;
}
export const useFeedback = create<FeedbackState>((set, get) => ({
  toasts: [],
  show(input) {
    const id = crypto.randomUUID();
    const toast = {
      ...input,
      id,
      order: ++order,
      expiresAt: Date.now() + (input.durationMs ?? (input.kind === "error" ? 12_000 : 5_000)),
    };
    set((state) => ({
      toasts: [toast, ...state.toasts.filter((row) => row.key !== input.key)].slice(0, 50),
    }));
    return id;
  },
  dismiss(id) {
    set((state) => ({ toasts: state.toasts.filter((row) => row.id !== id) }));
  },
  visible: () => visibleToasts(get().toasts),
  expire() {
    set((state) => ({ toasts: state.toasts.filter((row) => row.expiresAt > Date.now()) }));
  },
}));
